'use client';

import type { ReactNode } from 'react';
import { useEffect, useState } from 'react';
import { usePathname, useSearchParams } from 'next/navigation';
import AppNavigation from '@/app/Components/AppNavigation';
import { getPagePresentation } from '@/app/lib/chromeMode';
import InteractionFeedback from '@/app/Components/InteractionFeedback';
import { useAssistantPopup } from '@/app/Components/Assistant/AssistantContext';
import { resolveRouteTitle } from '@/app/lib/routeTitle';
import { AssistantArtifactHandoffNotice } from '@/app/Components/Assistant/AssistantArtifactHandoffNotice';
import { LoadingLogo } from '@/app/Components/LoadingBubbles';
import { wasOpenedInPlace } from '@/app/lib/inPlaceNavigation';
import {
  getRoutePresentationDataAttributes,
  getScrollOwnerOverflowClassName,
} from '@/app/lib/routePresentation';

const NAVIGATION_FALLBACK_MS = 12_000;

type PendingNavigation = {
  destination: string;
};

export default function AppShell({ children }: { children: ReactNode }) {
  const [hydrated, setHydrated] = useState(false);
  useEffect(() => setHydrated(true), []);
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const { isOpen: assistantOpen } = useAssistantPopup();
  const [mobileNavigationOpen, setMobileNavigationOpen] = useState(false);
  const [navigationPending, setNavigationPending] = useState<PendingNavigation | null>(null);
  const pagePresentation = getPagePresentation(pathname);
  const presentationDataAttributes = getRoutePresentationDataAttributes(pagePresentation);
  const chromeMode = pagePresentation.chromeMode;
  const scrollOverflowClassName = getScrollOwnerOverflowClassName(pagePresentation.scrollOwner);

  const routeKey = `${pathname}?${searchParams.toString()}`;

  useEffect(() => {
    setNavigationPending(null);
  }, [routeKey]);

  useEffect(() => {
    const handleNavigationClick = (event: MouseEvent) => {
      if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      const anchor = (event.target as Element | null)?.closest('a[href]');
      if (!anchor || anchor.getAttribute('target') === '_blank' || anchor.hasAttribute('download')) return;
      const href = new URL(anchor.getAttribute('href') || '', window.location.href);
      if (href.origin !== window.location.origin || (href.pathname === pathname && href.search === window.location.search)) return;
      // Links that open a view in place (the shared course view) mark the click
      // in their own handler, which runs after this capture listener.
      window.setTimeout(() => {
        if (!wasOpenedInPlace(event)) setNavigationPending({ destination: resolveRouteTitle(href.pathname) });
      }, 0);
    };
    document.addEventListener('click', handleNavigationClick, true);
    return () => document.removeEventListener('click', handleNavigationClick, true);
  }, [pathname]);

  useEffect(() => {
    if (!navigationPending) return;
    const timeout = window.setTimeout(() => setNavigationPending(null), NAVIGATION_FALLBACK_MS);
    return () => window.clearTimeout(timeout);
  }, [navigationPending]);

  if (chromeMode === 'chromeless') {
    return (
      <main
        {...presentationDataAttributes}
        data-app-shell-hydrated={hydrated ? "true" : undefined}
        id="main-scroll-container"
        inert={assistantOpen || undefined}
        aria-hidden={assistantOpen || undefined}
        className={`${pagePresentation.scrollOwner === 'document' ? 'h-screen h-dvh' : 'min-h-screen'} ${scrollOverflowClassName} bg-[var(--ss-canvas)]`}
      >
        <InteractionFeedback />
        {children}
      </main>
    );
  }

  return (
    <div
      {...presentationDataAttributes}
      data-app-shell=""
      data-app-shell-hydrated={hydrated ? "true" : undefined}
      inert={assistantOpen || undefined}
      aria-hidden={assistantOpen || undefined}
      className="ss-workspace-shell relative flex h-screen h-dvh overflow-hidden bg-[var(--ss-canvas)]"
    >
      <InteractionFeedback />
      <div aria-live="polite" aria-atomic="true" className="sr-only">{navigationPending ? `Opening ${navigationPending.destination}` : ''}</div>
      <div aria-hidden="true" className={`pointer-events-none fixed inset-x-0 top-0 z-[80] h-0.5 origin-left bg-red-500 transition-[opacity,transform] duration-200 motion-reduce:transition-none ${navigationPending ? 'scale-x-100 opacity-100' : 'scale-x-0 opacity-0'}`} />
      {navigationPending ? (
        <div className="fixed inset-0 z-[70] grid place-items-center bg-slate-950/30 px-5 backdrop-blur-[3px] motion-reduce:backdrop-blur-none" role="status" aria-label={`Opening ${navigationPending.destination}`}>
          <div className="w-full max-w-sm rounded-feature border border-white/60 bg-white/95 p-7 text-center shadow-e3 shadow-slate-950/20 dark:border-white/10 dark:bg-slate-950/95 dark:shadow-black/40">
            <LoadingLogo className="mx-auto" />
            <p className="mt-5 text-xs font-semibold uppercase tracking-[0.16em] text-slate-500 dark:text-slate-400">Opening</p>
            <h2 className="mt-1 text-2xl font-semibold tracking-tight text-slate-950 dark:text-white">{navigationPending.destination}</h2>
            <p className="mt-2 text-sm text-slate-600 dark:text-slate-300">Preparing your Scarlet Sync workspace…</p>
          </div>
        </div>
      ) : null}
      <AppNavigation onMobileOpenChange={setMobileNavigationOpen} />
      <main
        id="main-scroll-container"
        data-ss-scroll-owner={pagePresentation.scrollOwner}
        inert={mobileNavigationOpen || undefined}
        aria-hidden={mobileNavigationOpen || undefined}
        className={`ss-workspace-content relative z-10 min-w-0 flex-1 bg-transparent ${scrollOverflowClassName} ${pagePresentation.scrollOwner === 'map' ? 'p-0' : ''}`}
      >
        <AssistantArtifactHandoffNotice />
        {children}
      </main>
    </div>
  );
}
